import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BadgeCheck, Clock3, Mail, MapPin, Phone, QrCode, Send } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

import { ApiError } from '@starling/core/api/errors';
import { useSession } from '@/auth/hooks';
import { formatDate, formatPhone } from '@/lib/format';
import { whatsappUrl } from '@/lib/whatsapp';
import { retryAfterSeconds, useCountdown } from '@/lib/useCountdown';
import {
  CheckboxField,
  FormError,
  GhostButton,
  Hint,
  PrimaryButton,
  SelectField,
  TextField,
} from '@/components/form/primitives';
import { VerificationFlow } from './VerificationFlow';
import {
  useCreateOrganizationMutation,
  useMyOrganizationsQuery,
  useConfirmPhoneCodeMutation,
  useRequestPhoneCodeMutation,
  useTelegramLinkMutation,
  useUpdateOrganizationContactsMutation,
  type MyOrganization,
} from './hooks';
import styles from './MyOrganizationPage.module.css';

/** Интервал опроса, пока ждём подтверждения Telegram от бота. */
const TELEGRAM_POLL_MS = 2500;

// «Моя организация» (за сессией). Состояния:
//   * организаций нет → форма создания (type/name/city) — V1.3-a;
//   * есть, не верифицирована → карточка + флоу верификации (PostFlow);
//   * верифицирована → карточка со статусом ✓;
//   * несколько организаций → список с выбором (оптика может владеть и
//     мастерской — лимита «одна на личность» нет, решение V1.3-a №5).

type OrgType = MyOrganization['type'];

function typeLabel(t: (key: string) => string, type: OrgType): string {
  return t(`org.type.${type}`);
}

function OrganizationCard({
  organization,
  flat = false,
}: {
  organization: MyOrganization;
  /** Плоский вид — под списком выбора: без «кнопочной» карточки, чтобы
      детали выбранной организации не читались как ещё один пункт. */
  flat?: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const { user } = useSession();
  const verified = organization.verified_via !== null;

  return (
    <article className={`${styles.card} ${flat ? styles.cardFlat : ''}`}>
      <div className={styles.orgTop}>
        <h2 className={styles.orgName}>{organization.name}</h2>
        <span className={styles.typeTag}>{typeLabel(t, organization.type)}</span>
      </div>
      {organization.city && (
        <div className={styles.meta}>
          <MapPin size={12} aria-hidden="true" />
          {organization.city}
        </div>
      )}
      {user?.email && (
        // Email аккаунта из сессии (claim `email`): у владельца — тот, по
        // которому регистрировались. Google-only аккаунты email не хранят.
        <div className={styles.meta}>
          <Mail size={12} aria-hidden="true" />
          <span>{t('org.card.accountEmail', { email: user.email })}</span>
        </div>
      )}
      {organization.phone && (
        <div className={styles.meta}>
          {organization.whatsapp ? (
            // №22: телефон с WhatsApp — бейдж мессенджера, сам номер ведёт в
            // чат wa.me (та же подача, что на странице объявления).
            <a
              href={whatsappUrl(organization.phone)}
              target="_blank"
              rel="noreferrer noopener"
              className={`${styles.contactLink} ${styles.contactItem}`}
              title={t('org.card.whatsapp')}
            >
              <span className={`${styles.contactBadge} ${styles.badgeWhatsapp}`} aria-hidden="true">
                <Phone size={9} strokeWidth={2.5} />
              </span>
              {formatPhone(organization.phone)}
            </a>
          ) : (
            <span className={styles.contactItem}>
              <Phone size={12} aria-hidden="true" />
              {formatPhone(organization.phone)}
            </span>
          )}
          {organization.phone_verified_at && (
            <span className={styles.contactVerified}>{t('org.card.phoneVerified')}</span>
          )}
        </div>
      )}
      {organization.telegram && (
        <div className={styles.meta}>
          <a
            href={`https://t.me/${organization.telegram}`}
            target="_blank"
            rel="noreferrer noopener"
            className={`${styles.contactLink} ${styles.contactItem}`}
            title={t('org.card.telegramWrite')}
          >
            <span className={`${styles.contactBadge} ${styles.badgeTelegram}`} aria-hidden="true">
              <Send size={9} strokeWidth={2.5} />
            </span>
            @{organization.telegram}
          </a>
          {organization.telegram_verified_at && (
            <span className={styles.contactVerified}>{t('org.card.telegramVerified')}</span>
          )}
        </div>
      )}
      {verified ? (
        <div className={`${styles.badge} ${styles.badgeVerified}`}>
          <BadgeCheck size={15} aria-hidden="true" />
          {organization.verified_at
            ? t('org.card.verifiedAt', {
                date: formatDate(organization.verified_at, i18n.language),
              })
            : t('org.card.verified')}
        </div>
      ) : (
        <div className={`${styles.badge} ${styles.badgePending}`}>
          <Clock3 size={15} aria-hidden="true" />
          {t('org.card.notVerified')}
        </div>
      )}
      {/* Два разных подсказа: контактов ещё нет → «укажите»; есть, но ни один
          не подтверждён → «подтвердите» (замечание приёмки 2026-09-11). */}
      {verified && !organization.phone && !organization.telegram && (
        <div className={`${styles.badge} ${styles.badgePending}`}>
          <Clock3 size={15} aria-hidden="true" />
          {t('org.card.noContacts')}
        </div>
      )}
      {verified &&
        (organization.phone || organization.telegram) &&
        !organization.phone_verified_at &&
        !organization.telegram_verified_at && (
          <div className={`${styles.badge} ${styles.badgePending}`}>
            <Clock3 size={15} aria-hidden="true" />
            {t('org.card.contactsUnverified')}
          </div>
        )}
    </article>
  );
}

// Подпись поля контакта: название + галочка «подтверждён» (№16-C).
function ContactLabel({
  text,
  verified,
  verifiedText,
}: {
  text: string;
  verified: boolean;
  verifiedText: string;
}): JSX.Element {
  return (
    <>
      {text}
      {verified && (
        <span className={styles.labelCheck}>
          <BadgeCheck size={12} aria-hidden="true" />
          {verifiedText}
        </span>
      )}
    </>
  );
}

// Контакты организации (доводка V1 №16): телефон и Telegram — быстрый канал
// связи для объявлений; без хотя бы одного публикация закрыта. Форма всегда
// под карточкой выбранной организации (правит любой член).
// №16-C: сохранённый контакт нужно ПОДТВЕРДИТЬ — телефон SMS-кодом, Telegram
// через бота платформы (deep-link → Start → бот сверяет username).
function ContactsForm({ organization }: { organization: MyOrganization }): JSX.Element {
  const { t } = useTranslation();
  const mutation = useUpdateOrganizationContactsMutation(organization.id);
  const requestCode = useRequestPhoneCodeMutation(organization.id);
  const confirmCode = useConfirmPhoneCodeMutation(organization.id);
  const telegramLink = useTelegramLinkMutation(organization.id);
  const [phone, setPhone] = useState(organization.phone ?? '');
  const [whatsapp, setWhatsapp] = useState(organization.whatsapp);
  const [telegram, setTelegram] = useState(organization.telegram ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [phoneStep, setPhoneStep] = useState<'idle' | 'code'>('idle');
  const [code, setCode] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [tgUrl, setTgUrl] = useState<string | null>(null);
  const [tgError, setTgError] = useState<string | null>(null);
  // QR со ссылкой бота — открыть Telegram на телефоне, если сидим за десктопом
  // (как QR привязки клиента в Light).
  const [tgQr, setTgQr] = useState(false);
  // №20-в: таймер повторной отправки SMS-кода (сервер держит интервал 60 с).
  const [resendUntil, setResendUntil] = useState<number | null>(null);
  const resendIn = useCountdown(resendUntil);

  // Поля следуют за данными (после сохранения приходят нормализованные
  // значения — E.164, username без «@»); статусы сбрасываются только при
  // переключении организации, иначе подписи гасли бы сразу после действия.
  useEffect(() => {
    setPhone(organization.phone ?? '');
    setWhatsapp(organization.whatsapp);
    setTelegram(organization.telegram ?? '');
  }, [organization.id, organization.phone, organization.whatsapp, organization.telegram]);
  useEffect(() => {
    setSaved(false);
    setError(null);
    setPhoneStep('idle');
    setCode('');
    setPhoneError(null);
    setTgUrl(null);
    setTgError(null);
    setTgQr(false);
  }, [organization.id]);

  const phoneDirty = phone.trim() !== (organization.phone ?? '');
  // №22: флаг WhatsApp живёт у телефона — без номера он всегда снят (как на
  // бэкенде: очистка телефона сбрасывает `whatsapp`).
  const whatsappChecked = whatsapp && phone.trim() !== '';
  const whatsappDirty = whatsappChecked !== organization.whatsapp;
  const telegramDirty = telegram.trim() !== (organization.telegram ?? '');
  const dirty = phoneDirty || whatsappDirty || telegramDirty;
  // Пока показана ссылка бота и контакт не подтверждён — опрашиваем список
  // организаций: «Start» в боте отмечает подтверждение вебхуком, страница
  // сама об этом не узнает (как опрос статуса сообщения в Light).
  const tgWaiting = Boolean(tgUrl) && !organization.telegram_verified_at && !telegramDirty;
  const organizationsQuery = useMyOrganizationsQuery({
    pollIntervalMs: tgWaiting ? TELEGRAM_POLL_MS : false,
  });

  const submit = (): void => {
    setError(null);
    setSaved(false);
    mutation.mutate(
      { phone: phone.trim(), whatsapp: whatsappChecked, telegram: telegram.trim() },
      {
        onSuccess: () => {
          setSaved(true);
          setPhoneStep('idle');
          setTgUrl(null);
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            if (err.code === 'invalid_phone') return setError(t('org.contacts.invalidPhone'));
            if (err.code === 'invalid_telegram') return setError(t('org.contacts.invalidTelegram'));
            if (err.code === 'whatsapp_requires_phone')
              return setError(t('org.contacts.whatsappRequiresPhone'));
          }
          setError(t('org.contacts.error'));
        },
      },
    );
  };

  const startPhoneVerification = (): void => {
    setPhoneError(null);
    setCode('');
    requestCode.mutate(undefined, {
      onSuccess: () => {
        setPhoneStep('code');
        setResendUntil(Date.now() + 60_000);
      },
      onError: (err) => {
        if (err instanceof ApiError && err.code === 'resend_too_soon') {
          // Сервер знает точнее — берём его секунды.
          setResendUntil(Date.now() + retryAfterSeconds(err.details) * 1000);
          setPhoneStep('code');
          return setPhoneError(t('org.contacts.phoneVerify.tooSoon'));
        }
        if (err instanceof ApiError && err.code === 'rate_limited')
          return setPhoneError(t('org.contacts.phoneVerify.rateLimited'));
        setPhoneError(t('org.contacts.phoneVerify.error'));
      },
    });
  };

  const submitCode = (): void => {
    setPhoneError(null);
    confirmCode.mutate(
      { code: code.trim() },
      {
        onSuccess: () => {
          setPhoneStep('idle');
          setCode('');
        },
        onError: (err) => {
          if (err instanceof ApiError && err.code === 'contact_code_invalid')
            return setPhoneError(t('org.contacts.phoneVerify.invalidCode'));
          setPhoneError(t('org.contacts.phoneVerify.error'));
        },
      },
    );
  };

  const startTelegramVerification = (): void => {
    setTgError(null);
    telegramLink.mutate(undefined, {
      onSuccess: (link) => setTgUrl(link.url),
      onError: () => setTgError(t('org.contacts.telegramVerify.error')),
    });
  };

  const phoneSaved = Boolean(organization.phone) && !phoneDirty;
  const telegramSaved = Boolean(organization.telegram) && !telegramDirty;
  const pending = mutation.isPending || requestCode.isPending || confirmCode.isPending;

  return (
    <section className={`${styles.card} ${styles.contactsCard}`}>
      <h2 className={styles.formTitle}>{t('org.contacts.title')}</h2>
      <p className={styles.introText}>{t('org.contacts.text')}</p>
      <FormError>{error}</FormError>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <TextField
          label={
            <ContactLabel
              text={t('org.contacts.phone')}
              verified={phoneSaved && Boolean(organization.phone_verified_at)}
              verifiedText={t('org.contacts.verifiedMark')}
            />
          }
          value={phone}
          onChange={setPhone}
          type="tel"
          inputMode="tel"
          name="phone"
          placeholder={t('org.create.phonePlaceholder')}
          maxLength={32}
        />
        {phone.trim() !== '' && (
          <CheckboxField
            label={t('org.contacts.whatsapp')}
            checked={whatsappChecked}
            onChange={setWhatsapp}
            name="whatsapp"
          />
        )}
        {((phoneSaved && !organization.phone_verified_at && phoneStep === 'idle') ||
          (phoneDirty && phone.trim())) && (
          <div className={styles.verifyRow} data-testid="phone-verify">
            {phoneSaved && !organization.phone_verified_at && phoneStep === 'idle' && (
              <>
                <span className={styles.pendingNote}>{t('org.contacts.phoneVerify.pending')}</span>
                <GhostButton disabled={pending} onClick={startPhoneVerification}>
                  {t('org.contacts.phoneVerify.start')}
                </GhostButton>
              </>
            )}
            {phoneDirty && phone.trim() && (
              <span className={styles.pendingNote}>{t('org.contacts.saveToVerify')}</span>
            )}
          </div>
        )}
        {phoneSaved && !organization.phone_verified_at && phoneStep === 'code' && (
          <div className={styles.verifyBlock}>
            <p className={styles.pendingNote}>
              {t('org.contacts.phoneVerify.codeSent', { phone: organization.phone })}
            </p>
            <FormError>{phoneError}</FormError>
            <TextField
              label={t('org.contacts.phoneVerify.codeLabel')}
              value={code}
              onChange={setCode}
              inputMode="numeric"
              name="phone_code"
              autoComplete="one-time-code"
              placeholder="••••••"
              maxLength={6}
            />
            <div className={styles.verifyActions}>
              <GhostButton disabled={pending || !code.trim()} onClick={submitCode}>
                {t('org.contacts.phoneVerify.confirm')}
              </GhostButton>
              <GhostButton disabled={pending || resendIn > 0} onClick={startPhoneVerification}>
                {resendIn > 0
                  ? t('org.contacts.phoneVerify.resendIn', { seconds: resendIn })
                  : t('org.contacts.phoneVerify.resend')}
              </GhostButton>
            </div>
          </div>
        )}
        {phoneStep === 'idle' && phoneError && <FormError>{phoneError}</FormError>}

        <TextField
          label={
            <ContactLabel
              text={t('org.contacts.telegram')}
              verified={telegramSaved && Boolean(organization.telegram_verified_at)}
              verifiedText={t('org.contacts.verifiedMark')}
            />
          }
          value={telegram}
          onChange={setTelegram}
          name="telegram"
          placeholder={t('org.create.telegramPlaceholder')}
          maxLength={64}
        />
        {((telegramSaved && !organization.telegram_verified_at && !tgUrl) ||
          (telegramDirty && telegram.trim())) && (
          <div className={styles.verifyRow} data-testid="telegram-verify">
            {telegramSaved && !organization.telegram_verified_at && !tgUrl && (
              <>
                <span className={styles.pendingNote}>
                  {t('org.contacts.telegramVerify.pending')}
                </span>
                <GhostButton disabled={telegramLink.isPending} onClick={startTelegramVerification}>
                  {t('org.contacts.telegramVerify.start')}
                </GhostButton>
              </>
            )}
            {telegramDirty && telegram.trim() && (
              <span className={styles.pendingNote}>{t('org.contacts.saveToVerify')}</span>
            )}
          </div>
        )}
        {telegramSaved && !organization.telegram_verified_at && tgUrl && (
          <div className={styles.verifyBlock}>
            <p className={styles.pendingNote}>
              {t('org.contacts.telegramVerify.instructions', {
                username: organization.telegram,
              })}
            </p>
            <div className={styles.telegramLinkRow}>
              <a
                className={styles.telegramLink}
                href={tgUrl}
                target="_blank"
                rel="noreferrer noopener"
              >
                <Send size={13} aria-hidden="true" />
                {t('org.contacts.telegramVerify.open')}
              </a>
              <button
                type="button"
                className={styles.qrToggle}
                onClick={() => setTgQr((v) => !v)}
                aria-pressed={tgQr}
                aria-label={t('org.contacts.telegramVerify.qr')}
                title={t('org.contacts.telegramVerify.qr')}
              >
                <QrCode size={16} aria-hidden="true" />
              </button>
            </div>
            {tgQr && (
              <div className={styles.qrBox} data-testid="telegram-qr">
                <QRCodeSVG value={tgUrl} size={168} />
                <p className={styles.pendingNote}>{t('org.contacts.telegramVerify.qrHint')}</p>
              </div>
            )}
            <p className={styles.pendingNote}>{t('org.contacts.telegramVerify.waiting')}</p>
            <div className={styles.verifyActions}>
              <GhostButton
                disabled={organizationsQuery.isFetching}
                onClick={() => void organizationsQuery.refetch()}
              >
                {t('org.contacts.telegramVerify.check')}
              </GhostButton>
              <GhostButton disabled={telegramLink.isPending} onClick={startTelegramVerification}>
                {t('org.contacts.telegramVerify.newLink')}
              </GhostButton>
            </div>
          </div>
        )}
        <FormError>{tgError}</FormError>

        <PrimaryButton disabled={pending || !dirty}>{t('org.contacts.submit')}</PrimaryButton>
        {saved && <p className={styles.savedNote}>{t('org.contacts.saved')}</p>}
      </form>
    </section>
  );
}

function CreateOrganizationForm(): JSX.Element {
  const { t } = useTranslation();
  const mutation = useCreateOrganizationMutation();
  const [type, setType] = useState<OrgType>('optic');
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');
  const [telegram, setTelegram] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = (): void => {
    setError(null);
    mutation.mutate(
      {
        type,
        name: name.trim(),
        city: city.trim() || null,
        // Контакты (№16) — уходят только заполненные; без них организация
        // создастся, но публикация будет закрыта до заполнения.
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(telegram.trim() ? { telegram: telegram.trim() } : {}),
      },
      {
        onError: (err) => {
          if (err instanceof ApiError) {
            if (err.code === 'invalid_phone') return setError(t('org.contacts.invalidPhone'));
            if (err.code === 'invalid_telegram') return setError(t('org.contacts.invalidTelegram'));
          }
          setError(t('org.create.error'));
        },
      },
    );
  };

  return (
    <div className={styles.card}>
      <h2 className={styles.formTitle}>{t('org.create.title')}</h2>
      <p className={styles.introText}>{t('org.none.text')}</p>
      <FormError>{error}</FormError>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <SelectField
          label={t('org.create.type')}
          value={type}
          onChange={(next) => setType(next as OrgType)}
          name="type"
          options={[
            { value: 'optic', label: t('org.type.optic') },
            { value: 'workshop', label: t('org.type.workshop') },
            { value: 'supplier', label: t('org.type.supplier') },
          ]}
        />
        <TextField
          label={t('org.create.name')}
          value={name}
          onChange={setName}
          name="name"
          placeholder={t('org.create.namePlaceholder')}
          maxLength={200}
        />
        <TextField
          label={t('org.create.city')}
          value={city}
          onChange={setCity}
          name="city"
          placeholder={t('org.create.cityPlaceholder')}
          maxLength={100}
        />
        <Hint>{t('org.create.contactsHint')}</Hint>
        <TextField
          label={t('org.create.phone')}
          value={phone}
          onChange={setPhone}
          type="tel"
          inputMode="tel"
          name="phone"
          placeholder={t('org.create.phonePlaceholder')}
          maxLength={32}
        />
        <TextField
          label={t('org.create.telegram')}
          value={telegram}
          onChange={setTelegram}
          name="telegram"
          placeholder={t('org.create.telegramPlaceholder')}
          maxLength={64}
        />
        <PrimaryButton disabled={mutation.isPending || !name.trim()}>
          {t('org.create.submit')}
        </PrimaryButton>
      </form>
    </div>
  );
}

export function MyOrganizationPage(): JSX.Element {
  const { t } = useTranslation();
  const query = useMyOrganizationsQuery();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (query.isPending) {
    return (
      <div className={styles.wrap}>
        <h1 className={styles.pageTitle}>{t('org.title')}</h1>
        <div className={styles.state}>{t('org.loading')}</div>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className={styles.wrap}>
        <h1 className={styles.pageTitle}>{t('org.title')}</h1>
        <div className={`${styles.state} ${styles.stateError}`}>
          {t('org.error')}
          <div>
            <button type="button" className={styles.retry} onClick={() => void query.refetch()}>
              {t('org.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const items = query.data.items;

  if (items.length === 0) {
    return (
      <div className={styles.wrap}>
        <h1 className={styles.pageTitle}>{t('org.title')}</h1>
        <CreateOrganizationForm />
      </div>
    );
  }

  const selected = items.find((org) => org.id === selectedId) ?? items[0];

  return (
    <div className={styles.wrap}>
      <h1 className={styles.pageTitle}>{t('org.title')}</h1>

      {items.length > 1 && (
        <>
          <p className={styles.listLabel}>{t('org.list.title')}</p>
          <div className={styles.list}>
            {items.map((org) => (
              <button
                key={org.id}
                type="button"
                className={`${styles.listItem} ${
                  org.id === selected?.id ? styles.listItemActive : ''
                }`}
                aria-pressed={org.id === selected?.id}
                onClick={() => setSelectedId(org.id)}
              >
                <span className={styles.listItemName}>
                  {org.verified_via !== null ? (
                    <BadgeCheck size={14} aria-hidden="true" className={styles.listItemVerified} />
                  ) : (
                    <Clock3 size={14} aria-hidden="true" className={styles.listItemPending} />
                  )}
                  {org.name}
                </span>
                <span className={styles.typeTag}>{typeLabel(t, org.type)}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {selected && (
        <>
          {items.length > 1 && <p className={styles.listLabel}>{t('org.list.selectedTitle')}</p>}
          <OrganizationCard organization={selected} flat={items.length > 1} />
          {selected.verified_via === null && <VerificationFlow organizationId={selected.id} />}
          <ContactsForm organization={selected} />
        </>
      )}
    </div>
  );
}
